import { AlertFactory, AlertState } from 'alerts/Alert'
import Member from 'iam/models/Member'
import Team from 'iam/models/Team'
import once from 'lodash/once'
import orderBy from 'lodash/orderBy'
import { TextMessageContentViewModel } from 'messaging/components/ConversationView/Messages/List/TextMessageContent/TextMessageContentViewModel'
import {
  FetchMessageForConversationQuery,
  SendMessageParams,
} from 'messaging/ConversationStore'
import Conversation from 'messaging/models/Conversation'
import Message from 'messaging/models/Message'
import { MessageType } from 'messaging/models/MessageType'
import { Participant } from 'messaging/models/Participant'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { createTransformer } from 'mobx-utils'
import { CursorPaged, CursorPageQuery, Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { downloadPdf, PDFElements } from 'utils/pdf-utils'
import JobProgressionContentViewModel from './Messages/List/ProgressionMessageContent/JobProgressionMessageContentViewModel'
import MessageViewModel, { MessageContentViewModel } from './MessageViewModel'
import { Forbidden } from 'fejl'
import Job from 'jobs/Job'
import { GetCurrentMember } from 'iam/SessionStore'
import { isSameDay } from 'date-fns'
import { BehaviorSubject } from 'rxjs'

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: '2-digit',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

/**
 * Function that takes in only the context to create a VM.
 */
export type ConversationViewModelFactory = (
  conversation: Conversation,
  team: Team,
  conversationViewModelAdapter: ConversationViewModelPort,
  job: Job,
) => ConversationViewModel

/**
 * Conversation view model.
 */
export default class ConversationViewModel {
  /**
   * Tracks the VM's active status.
   *
   * @private
   */
  private readonly _active = new BehaviorSubject(false)

  /**
   * The text being written by the user.
   */
  @observable composerText = ''

  /**
   * The message id in edit mode.
   */
  @observable editingMessageId = Id.none

  /**
   * Constructor.
   */
  constructor(
    public conversation: Conversation,
    private team: Team = Team.none,
    private adapter: ConversationViewModelPort,
    private getCurrentMember: GetCurrentMember,
  ) {
    makeObservable(this)
    this.fetchInitial = once(this.fetchInitial)
  }

  @computed
  get canRefreshManually() {
    const currentMember = this.getCurrentMember(this.conversation.workspace.id)
    const isAdmin = currentMember.hasPermission('ADMIN_JOB_VIEW')
    const isPartOfConvo = this.conversation.participants.some(
      (p) => p.member.user.publicId === currentMember.user.publicId,
    )

    return isAdmin && !isPartOfConvo
  }

  @computed
  get shouldShowMessageBank() {
    const isEnabledAtWorkspace =
      this.conversation.workspace.features.messageBankConfig.enabled

    return isEnabledAtWorkspace
  }

  /**
   * Gets the unread count.
   */
  @computed
  get unreadCount() {
    return this.conversation?.unreadCount ?? 0
  }

  /**
   * Creates a Message View Model.
   */
  createMessageViewModel = createTransformer<Message, MessageViewModel>(
    (message) => {
      const vm = new MessageViewModel(
        message,
        this.createMessageContentViewModel(message),
      )
      vm.activate().then()
      return vm
    },
    (vm) => vm && vm.deactivate(),
  )

  /**
   * Conversation scroller.
   */
  scroller = new InfiniteScrollProvider({
    limit: 20,
    fetchItems: (params) => {
      return Forbidden.retry(() => this.fetchMessages(params), {
        tries: 10,
        minTimeout: 400,
        maxTimeout: 1_000,
      })
    },
  })

  /**
   * The names of the people currently typing.
   */
  @computed
  get peopleCurrentlyTyping(): string[] {
    return []
  }

  /**
   * Conversation messages as view models.
   */
  @computed
  get messages() {
    return orderBy(
      this.conversation.messages.map(this.createMessageViewModel),
      (vm) => vm.message.dateCreated,
    )
  }

  /**
   * Message groups, grouping messages from the same sender together.
   */
  @computed
  get messageGroups() {
    const messages = this.messages.slice()
    if (messages.length === 0) {
      return []
    }

    const groups: MessageGroup[] = []
    let currentGroup: MessageGroup | null = null
    let previousMessage: MessageViewModel | null = null
    for (const idx in messages) {
      const message = messages[idx]
      const midnightPassedSincePreviousMessage = Boolean(
        previousMessage &&
          !isSameDay(
            previousMessage.message.dateCreated,
            message.message.dateCreated,
          ),
      )
      const isNewSender = Boolean(
        !currentGroup || message.message.sender !== currentGroup.sender,
      )
      const isNonTextContent =
        message.message.content.type !== 'Text' ||
        (previousMessage && previousMessage.message.content.type !== 'Text')

      if (
        !currentGroup ||
        isNewSender ||
        isNonTextContent ||
        midnightPassedSincePreviousMessage
      ) {
        currentGroup = {
          sender: message.message.sender,
          messages: [],
          timestamp: message.message.dateCreated,
          tag: MessageGroupTag.None,
          showDateLine:
            groups.length === 0 || midnightPassedSincePreviousMessage,
        }
        groups.push(currentGroup)
      }

      const tag = groupTagForMessage(message)
      if (currentGroup.tag === MessageGroupTag.None) {
        currentGroup.tag = tag
      }

      currentGroup.messages.push(message)
      previousMessage = message
    }
    return groups
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._active.next(true)
    this.fetchInitial().then()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this._active.next(false)
  }

  /**
   * Sets the conversation.
   */
  @action.bound
  setConversation(conversation: Conversation) {
    this.conversation = conversation
  }

  /**
   * Sets the message id which will be in edit mode.
   */
  @action.bound
  setEditingMessageId(messageId: Id) {
    this.editingMessageId = messageId
  }

  /**
   * Checks whether the message can be edited.
   */
  @computed
  get canEditMessage() {
    return this.getCurrentMember().user.platformAdmin
  }

  /**
   * Fetches the conversation and initial messages.
   */
  async fetchInitial() {
    if (this.conversation !== Conversation.none) {
      await this.scroller.fetch()
    }
  }

  /**
   * Sends a message.
   */
  async sendMessage() {
    const text = this.composerText
    if (!text) {
      return
    }
    this.setComposerText('')
    await this.adapter.sendMessage(this.conversation, {
      message: text,
      team: this.team,
      type: MessageType.Message,
    })
  }

  /**
   * Updates the text of a message.
   */
  updateMessage = task.resolved(async (messageId: Id, text: string) => {
    if (!text) {
      return
    }
    try {
      await this.adapter.updateMessage(this.conversation, messageId, text)
      this.setEditingMessageId(Id.none)
    } catch (error) {
      this.adapter.newAlert().fromError(error).show()
    }
  })

  fetchLatestMessages = task.resolved(async () => {
    const msg = this.adapter
      .newAlert()
      .saying('Fetching latest messages!', 'Please wait')
      .show()
    await this.scroller.fetch()
    msg
      .withState(AlertState.Success)
      .saying('Success!', `You're now viewing the latest messages!`)
      .autoDismiss(3000)
  })

  /**
   * Downloads the conversation as PDF
   */
  @action.bound
  async downloadConversation() {
    const msg = this.adapter
      .newAlert()
      .saying('Creating PDF.', 'Please hold on.')
      .show()

    while (!this.scroller.isDone) {
      await this.scroller.more()
    }

    const pdfElements: PDFElements[] = [
      {
        type: 'title',
        text: `Conversation History`,
      },
      ...this.messages.map(
        (m) =>
          ({
            type: 'text',
            text: `${dateFormatter.format(m.message.dateCreated)} - ${
              m.message.sender.displayName
            }: ${m.message.text}`,
          }) as PDFElements,
      ),
    ]

    await downloadPdf(
      pdfElements,
      `Conversation-${dateFormatter.format(new Date())}`,
    )

    msg
      .withState(AlertState.Success)
      .saying(`PDF has been created!`, `Your download will begin shortly.`)
      .autoDismiss()
      .show()
  }

  /**
   * Sets the composer text.
   *
   * @param text
   */
  @action.bound
  setComposerText(text: string) {
    this.composerText = text
  }

  /**
   * Marks the message as read.
   * @param message
   */
  markAsRead(message: MessageViewModel) {
    this.adapter.markAsRead(message.message)
  }

  /**
   * Toggles muting on the conversation.
   */
  async toggleMute() {
    const participant = await this.adapter
      .toggleMute(this.conversation)
      .catch((error) => {
        this.adapter.newAlert().fromError(error).show()
        console.error(error)
        return null
      })

    if (participant) {
      this.adapter
        .newAlert()
        .withState(AlertState.Success)
        .saying(
          `Success!`,
          `Conversation notifications ` +
            (participant.muted ? ' muted!' : ' unmuted!'),
        )
        .autoDismiss()
        .show()
    }
  }

  /**
   * Fetches the messages.
   *
   * @param params
   * @private
   */
  private async fetchMessages(params: CursorPageQuery) {
    const page = await this.adapter.fetchMessages({
      conversationId: Id.assert(this.conversation.id),
      teamId: this.team.id,
      ...params,
    })

    return page
  }

  /**
   * Creates a content view model based on the message content type.
   *
   * @param message
   * @private
   */
  private createMessageContentViewModel(
    message: Message,
  ): MessageContentViewModel {
    switch (message.content.type) {
      case 'Progression':
        return new JobProgressionContentViewModel(
          this.adapter.jobProgressionAdapter.canUpdateJobProgressionAsClient,
          message,
          this.adapter.jobProgressionAdapter.viewRejectDraftInReviewDialog,
          this.adapter.jobProgressionAdapter.viewApproveDraftInReviewDialog,
        )
      default:
        return new TextMessageContentViewModel(message)
    }
  }
}

/**
 * Functionality needed from other parts of the system.
 */
export interface ConversationViewModelPort {
  newAlert: AlertFactory
  fetchMessages(
    query: FetchMessageForConversationQuery,
  ): Promise<CursorPaged<Message>>
  sendMessage(
    conversation: Conversation,
    params: SendMessageParams,
  ): Promise<void>
  updateMessage(
    conversation: Conversation,
    messageId: Id,
    text: string,
  ): Promise<void>
  markAsRead(message: Message): void
  toggleMute(conversation: Conversation): Promise<Participant | null>
  jobProgressionAdapter: JobProgressionConversationAdapter
}

/**
 * Adapter for Job Progression related callbacks
 */
export interface JobProgressionConversationAdapter {
  canUpdateJobProgressionAsClient: boolean
  viewApproveDraftInReviewDialog: () => void
  viewRejectDraftInReviewDialog: () => void
}

/**
 * Message group.
 */
export interface MessageGroup {
  sender: Member
  messages: MessageViewModel[]
  timestamp: Date
  showDateLine: boolean
  tag: MessageGroupTag
}

/**
 * Tag to add to the message group.
 */
export enum MessageGroupTag {
  None,
  Admin,
  System,
  Assistant,
}

/**
 * Compute the group tag for a message.
 *
 * @param message
 */
function groupTagForMessage(message: MessageViewModel): MessageGroupTag {
  switch (message.messageType) {
    case MessageType.AdminMessage:
      return MessageGroupTag.Admin

    case MessageType.SystemMessage:
      return MessageGroupTag.System

    case MessageType.ExternalReference:
      switch (message.contentType) {
        case 'Assistant':
          return MessageGroupTag.Assistant

        default:
          return MessageGroupTag.None
      }

    default:
      return MessageGroupTag.None
  }
}
